/**
 * Responsibility: say where a river channel runs, how deeply it cuts, and where its water sits.
 *
 * ## Why a river has to be carved rather than flooded
 *
 * The intuitive model — put a water level over low ground and let the hollows fill — does not produce a river. The low
 * regions of an fBm field are **blobs**, so that gives scattered ponds. A river is a *line*, and lines are hard to get
 * out of noise.
 *
 * `Math.abs(field)` solves it, exactly as it did for canyons. The absolute value is near zero along the field's
 * **zero-crossing contours**, and the zero set of a 2D noise field is a family of winding, branching, continuous
 * curves. So "close to zero" means "inside the valley", and a whole drainage network costs one noise sample. It is
 * evaluable at any coordinate with no path tracing and no accumulated state, which is what determinism requires.
 *
 * ## Separated from the biome on purpose
 *
 * A river is not a kind of land, it is something that crosses land. Keeping the channel here means a second biome can
 * adopt the same water later — a wooded valley, or a city built up either side of one — by subtracting the same
 * incision from its own elevation, with no copy of this arithmetic anywhere.
 *
 * ## What this deliberately does not do
 *
 * There is no downstream direction. A contour has no flow, so the water level here follows the regional base rather
 * than descending, and a river will not visibly run downhill. Real gradients are about a tenth of a percent, so at
 * this scale that is invisible — but it is the reason there are no waterfalls, rapids or deltas, and it is why every
 * branch is the same width when a real tributary would be narrower than its trunk.
 *
 * Contains no Three.js and no DOM access, so it is fully unit testable.
 */

import { createFbm } from './noise.js';
import { carveHollow } from './hollow.js';
import { RIVER } from '../config.js';

/**
 * Build the channel geometry for a seed.
 *
 * @param {number} seed - Base world seed.
 * @param {string} biomeId - Derives an independent stream, so two biomes adopting rivers do not put them in identical
 *   places where they happen to meet.
 * @returns {{fromCentre: Function, carve: Function, waterLevel: Function, deepestFloor: Function}}
 */
export function createRiverChannel(seed, biomeId) {
  /*
    Used *signed*, unlike most fields here, because it is the zero crossing that matters rather than the magnitude.
  */
  const channel = createFbm(seed, `channel:river:${biomeId}`, {
    octaves: RIVER.CHANNEL_OCTAVES,
    baseFrequency: 1 / RIVER.CHANNEL_WAVELENGTH,
    persistence: RIVER.CHANNEL_PERSISTENCE,
    lacunarity: RIVER.LACUNARITY,
  });

  return {
    /**
     * Distance from the channel's centre line, as a fraction: 0 on the line, 1 at the valley edge, above 1 outside.
     *
     * Measured in field value rather than in world units, which is what makes the valley naturally narrow where the
     * field changes quickly and open out where it is flat.
     *
     * @param {number} x
     * @param {number} z
     * @returns {number}
     */
    fromCentre(x, z) {
      return Math.abs(channel(x, z)) / RIVER.CHANNEL_WIDTH;
    },

    /**
     * Elevation inside the valley, blending the channel floor up to the surrounding land at the bank.
     *
     * **The floor is set by `base` alone and ignores `land`.** That is what makes this read as a valley rather than as
     * a groove pressed into a hillside: a real river's grade does not care how tall the hills it cuts through are. The
     * visible consequence is that the water comes out wider where the land is low and narrower where a hill crowds it,
     * with nothing in the code saying so.
     *
     * @param {number} land - Surrounding ground height, base plus hills.
     * @param {number} base - Regional valley floor, without hills.
     * @param {number} fromCentre - From `fromCentre` above.
     * @returns {number}
     */
    carve(land, base, fromCentre) {
      /*
        Shared with the lake basin, because a channel and a basin are the same hollow with a different mask. See
        `carveHollow` for why `BANK_SHARPNESS` turns out to control the width of the water as much as the shape of the
        ground.
      */
      return carveHollow(land, base - RIVER.INCISION, fromCentre, RIVER.BANK_SHARPNESS);
    },

    /**
     * Height of the water surface for a given regional base.
     *
     * A function of the base only, never of the local land. So the water is level across the channel and drifts only
     * with the regional field, which is the closest thing to a water table available without simulating one.
     *
     * @param {number} base
     * @returns {number}
     */
    waterLevel(base) {
      return base - RIVER.WATER_DROP;
    },

    /**
     * Deepest the water can be, in world units.
     *
     * Exported because it has to stay under the camera's ground clearance — that is what guarantees nothing can ever
     * pass beneath the surface, and therefore why the flight model needs to know nothing about water.
     *
     * @returns {number}
     */
    maxDepth() {
      return RIVER.INCISION - RIVER.WATER_DROP;
    },
  };
}
